import { useDroppable } from '@dnd-kit/react';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { WebsiteInfo } from '../utils/formatWebsiteInfo';
import styles from './SourcesDroppableArea.module.scss';
import { WebsiteCard } from './WebsiteCard';

export const MAX_CUSTOM_SOURCES = 50;
const QUICK_ANSWER_SOURCES_LIMIT = 20;
const AREA_ID = 'sources-draggable-area';

interface SourcesDroppableAreaProps {
  items: WebsiteInfo[];
  handleRemove: (id: string) => void;
}

export const SourcesDroppableArea = ({
  items,
  handleRemove,
}: SourcesDroppableAreaProps) => {
  const { t } = useTranslation(['chat']);

  const droppable = useDroppable({ id: AREA_ID });

  const sourcesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sourcesContainerRef.current && items.length) {
      sourcesContainerRef.current.scrollTop =
        sourcesContainerRef.current.scrollHeight;
    }
  }, [items.length]);

  return (
    <div id={AREA_ID} ref={droppable.ref}>
      <h4 className={styles.sourcesHeader}>
        {t('chat:trustedSources.modal.tenSourcesHeader', {
          count: items.slice(0, QUICK_ANSWER_SOURCES_LIMIT).length,
        })}
      </h4>
      <div className={styles.sourcesList} ref={sourcesContainerRef}>
        {items.slice(0, QUICK_ANSWER_SOURCES_LIMIT).map((website, i) => (
          <WebsiteCard
            key={website.id}
            index={i}
            {...website}
            handleRemove={handleRemove}
          />
        ))}
        <h4
          className={clsx(styles.sourcesHeader, styles.stickySourcesHeader, {
            [styles.isLast]: items.length <= QUICK_ANSWER_SOURCES_LIMIT,
          })}
        >
          {t('chat:trustedSources.modal.upToTwentySourcesHeader', {
            count: items.slice(QUICK_ANSWER_SOURCES_LIMIT).length,
          })}
        </h4>
        {items.slice(QUICK_ANSWER_SOURCES_LIMIT).map((website, i) => (
          <WebsiteCard
            key={website.id}
            index={QUICK_ANSWER_SOURCES_LIMIT + i}
            {...website}
            handleRemove={handleRemove}
          />
        ))}
      </div>
    </div>
  );
};
